import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import { Pencil, Trash2, HelpCircle } from 'lucide-react';
import { EnhancedAccordian } from '../../../../../ui';
import { AddQuestionModal } from '../../../../../ui/add_question_modal/add_question_modal';
import { Modal } from '../../../../../ui/modal/modal';
import { addQuestionToAssessment } from '../../../../../utils/api_service/assessment_api';
import { addQuestion } from '../../../../../utils/api_service/job_api';
import {
  convertSecondsToTimeTaken,
  convertStringToUnix,
} from '../../../../../utils/helpers/time';
import {
  Question,
  QuestionInfoTotalCount,
  SubSkill,
} from '../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { SelectedQuestionContent } from './Containers/Components';
import SelectQuestions from './Containers/SelectQuestions';
import { useQuestions } from './Hooks';

interface IQuestions {
  assessmentId: string;
  skillList?: SelectOptions[];
  levelList?: SelectOptions[];
  jobTitleList?: SelectOptions[];
  subSkillList?: SubSkill[];
  setSkillList: (opts: SelectOptions[]) => void;
}

export const Questions: React.FC<IQuestions> = ({
  assessmentId,
  skillList,
  levelList,
  jobTitleList,
  subSkillList,
  setSkillList,
}) => {
  const {
    expandedIds,
    questionList,
    editQuestion,
    deleteQuestion,
    handleExpandAll,
    handleOpenSelectQuestions,
    handleDragEnd,
    toggleExpand,
    isOpenSelectQuestions,
    handleCloseSelectQuestions,
    handleAddQuestion,
    selectedQuestions,
    handleSaveSelection,
    setQuestionList,
  } = useQuestions(assessmentId);

  const [editMap, setEditMap] = useState<Record<string, boolean>>({});
  const setDisabled = (id: string, disabled: boolean) =>
    setEditMap((m) => ({ ...m, [id]: disabled }));

  const questionTypes: SelectOptions[] = [
    { value: 'Modern', label: 'Video' },
    { value: 'Classic', label: 'Text' },
  ];
  const [totalCount, setTotalCount] = useState<QuestionInfoTotalCount>({
    totalPoints: 0,
    totalTime: '00:00',
  });
  const [addQuestionModalOpen, setAddQuestionModalOpen] =
    useState<boolean>(false);

  const handleChange = (question: Question) => {
    addQuestionToAssessment(
      questionList.length.toString(),
      question.id,
      assessmentId,
    ).then((response) => {
      const newQuestion = Object.assign(
        { assessmentQuestionId: response.id },
        question,
      );
      setQuestionList((ql) => [...ql, newQuestion]);
      const prevUnix = convertStringToUnix(totalCount.totalTime) || 0;
      const newUnix = prevUnix + question.time;
      setTotalCount({
        totalPoints: totalCount.totalPoints + question.points,
        totalTime: convertSecondsToTimeTaken(newUnix) || '00:00',
      });
    });
  };

  const addNewQuestion = (question: Question) => {
    addQuestion(question).then((response) => {
      handleChange(response);
      setAddQuestionModalOpen(false);
    });
  };
  const toggleAddQuestionModal = () => setAddQuestionModalOpen(true);

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ py: 2 }}
      >
        <Typography variant="h6" fontWeight={700} color="primary">
          Questions
        </Typography>

        {questionList.length !== 0 && (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label={`Total Score: ${totalCount.totalPoints}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Duration: ${totalCount.totalTime} min`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${questionList.length} questions`}
              color="primary"
              variant="outlined"
            />
          </Stack>
        )}

        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            onClick={handleExpandAll}
            sx={{ textTransform: 'none' }}
          >
            {expandedIds.length === questionList.length
              ? 'Collapse All'
              : 'Expand All'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenSelectQuestions}
            sx={{ textTransform: 'none' }}
          >
            Select Questions
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={toggleAddQuestionModal}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Create New
          </Button>
        </Stack>
      </Stack>

      {/* Empty State */}
      {questionList.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ py: 10 }}
        >
          <HelpCircle size={48} color="#9e9e9e" />
          <Typography variant="body1" color="text.secondary">
            No questions selected yet
          </Typography>
        </Stack>
      ) : (
        /* Drag & Drop List */
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="questions">
            {(prov) => (
              <Box {...prov.droppableProps} ref={prov.innerRef}>
                {questionList.map((q, i) => {
                  const isDisabled = editMap[q.id] ?? true;
                  const onEditSave = (updated: Question) => {
                    setDisabled(q.id, true);
                    editQuestion(updated);
                  };
                  const returnSelectedJobTitles = () =>
                    (q.jobTitle ?? [])
                      .map((id) => {
                        const found = jobTitleList?.find((j) => j.value === id);
                        return found
                          ? { value: found.value, label: found.label }
                          : null;
                      })
                      .filter((x): x is SelectOptions => Boolean(x));

                  return (
                    <Draggable key={q.id} draggableId={q.id} index={i}>
                      {(p) => (
                        <Box
                          ref={p.innerRef}
                          {...p.draggableProps}
                          {...p.dragHandleProps}
                          sx={{ mb: 2 }}
                        >
                          <EnhancedAccordian
                            title={q.question}
                            isExpanded={expandedIds.includes(q.id)}
                            onToggle={() => toggleExpand(q.id)}
                            rightIcons={[
                              <IconButton
                                key="edit"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDisabled(q.id, false);
                                  if (!expandedIds.includes(q.id))
                                    toggleExpand(q.id);
                                }}
                              >
                                <Pencil size={16} />
                              </IconButton>,
                              <IconButton
                                key="delete"
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (q.assessmentQuestionId) {
                                    deleteQuestion(q.assessmentQuestionId);
                                  }
                                }}
                              >
                                <Trash2 size={16} />
                              </IconButton>,
                            ]}
                          >
                            <SelectedQuestionContent
                              toggledQuestion={q}
                              isDisabled={isDisabled}
                              setIsDisabled={(d) => setDisabled(q.id, d)}
                              editQuestion={onEditSave}
                              onAddNewSkill={() => {}}
                              returnSelectedJobTitles={returnSelectedJobTitles}
                              skills={skillList || []}
                              subSkills1={
                                subSkillList
                                  ?.filter((s) => s.parentSkillId === q.skillId)
                                  .map((s) => ({
                                    value: s.id,
                                    label: s.subskillName,
                                  })) || []
                              }
                              subSkills2={
                                subSkillList
                                  ?.filter(
                                    (s) => s.parentSkillId === q.subSkill1Id,
                                  )
                                  .map((s) => ({
                                    value: s.id,
                                    label: s.subskillName,
                                  })) || []
                              }
                              subSkills3={
                                subSkillList
                                  ?.filter(
                                    (s) => s.parentSkillId === q.subSkill2Id,
                                  )
                                  .map((s) => ({
                                    value: s.id,
                                    label: s.subskillName,
                                  })) || []
                              }
                              levelList={levelList}
                              jobTitleList={jobTitleList || []}
                              questionTypes={questionTypes}
                            />
                          </EnhancedAccordian>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {prov.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Select & Create Modals */}
      <SelectQuestions
        isOpen={isOpenSelectQuestions}
        onCloseDrawer={handleCloseSelectQuestions}
        onSave={handleSaveSelection}
        onAddQuestion={handleAddQuestion}
        initialSelected={selectedQuestions}
        assessmentId={assessmentId}
        skillList={skillList}
        levelList={levelList}
        subSkillList={subSkillList}
        jobTitleList={jobTitleList}
      />

      <Modal
        visible={addQuestionModalOpen}
        modalContent={
          <AddQuestionModal
            addNewQuestion={addNewQuestion}
            skillList={skillList || []}
            levelList={levelList || []}
            jobTitleList={jobTitleList || []}
            subSkillList={subSkillList}
            setSkillList={setSkillList}
          />
        }
        toggle={() => setAddQuestionModalOpen(false)}
      />
    </Box>
  );
};
